import type { AsyncSequence } from "../types/AsyncSequence"

import { Broadcast } from "../classes/Broadcast"
import { ProcessIterable } from "./ProcessIterable"

/**
 * Creates multiple independent copies of an async iterable that all receive the same values.
 * Each copy can be consumed independently without affecting the others.
 *
 * @param input - The source async iterable to copy
 * @param length - Number of copies to create
 * @returns Array of independent async generators that all yield the same values
 *
 * @example
 * ```ts
 * import { CopyIterable } from "@pdfking/tfunc"
 *
 * const array = [1, 2, 3, 4, 5];
 * const copies = CopyIterable(array, 3);
 *
 * // All three copies will independently yield: 1, 2, 3, 4, 5
 * for (const copy of copies) {
 *   const values = await IterableToArray(copy);
 *   console.log(values); // [1, 2, 3, 4, 5]
 * }
 * ```
 */
export const CopyIterable = <T>(
  input: AsyncSequence<T>,
  length: number
): AsyncGenerator<T>[] => {
  const { push, stop, subscribe } = Broadcast<T>()
  const result = Array.from({ length }, subscribe)
  void ProcessIterable(input, push, stop)
  return result
}
